import { useState, useEffect } from 'react';
import { Meeting } from '@/types/meeting';
import { mockMeetings } from '../data/meetingMocks';

const STORAGE_KEY = 'school-meetings';

export function useMeetings() {
  const [meetings, setMeetings] = useState<Meeting[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return mockMeetings;
    
    const parsedMeetings = JSON.parse(stored);
    return parsedMeetings.map((meeting: any) => ({
      ...meeting,
      startTime: new Date(meeting.startTime),
      endTime: new Date(meeting.endTime),
      tasks: meeting.tasks?.map((task: any) => ({
        ...task,
        dueDate: new Date(task.dueDate)
      })) || []
    }));
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(meetings));
  }, [meetings]);

  const updateMeeting = (updatedMeeting: Partial<Meeting> & { id: string }) => {
    setMeetings(prevMeetings =>
      prevMeetings.map(meeting =>
        meeting.id === updatedMeeting.id
          ? { ...meeting, ...updatedMeeting }
          : meeting
      )
    );
  };

  const addMeeting = (newMeeting: Omit<Meeting, 'id'>) => {
    if (newMeeting.recurrence) {
      const meetings: Meeting[] = [];
      let currentDate = new Date(newMeeting.startTime);
      let count = 0;
      
      while (
        (!newMeeting.recurrence.endDate || currentDate <= newMeeting.recurrence.endDate) &&
        (!newMeeting.recurrence.occurrences || count < newMeeting.recurrence.occurrences)
      ) {
        if (
          newMeeting.recurrence.frequency === 'WEEKLY' &&
          newMeeting.recurrence.daysOfWeek?.length &&
          !newMeeting.recurrence.daysOfWeek.includes(currentDate.getDay())
        ) {
          currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
          continue;
        }

        const meetingDuration = new Date(newMeeting.endTime).getTime() - new Date(newMeeting.startTime).getTime();
        const meeting: Meeting = {
          ...newMeeting,
          id: Math.random().toString(36).substr(2, 9),
          startTime: new Date(currentDate),
          endTime: new Date(currentDate.getTime() + meetingDuration),
        };
        meetings.push(meeting);
        count++;

        // Calculate next date based on frequency
        switch (newMeeting.recurrence.frequency) {
          case 'DAILY':
            currentDate = new Date(currentDate.setDate(
              currentDate.getDate() + newMeeting.recurrence.interval
            ));
            break;
          case 'WEEKLY':
            if (!newMeeting.recurrence.daysOfWeek?.length) {
              currentDate = new Date(currentDate.setDate(
                currentDate.getDate() + (7 * newMeeting.recurrence.interval)
              ));
            } else {
              currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
            }
            break;
          case 'MONTHLY':
            currentDate = new Date(currentDate.setMonth(
              currentDate.getMonth() + newMeeting.recurrence.interval
            ));
            break;
        }
      }

      setMeetings(prevMeetings => [...prevMeetings, ...meetings]);
    } else {
      const meeting: Meeting = {
        ...newMeeting,
        id: Math.random().toString(36).substr(2, 9),
      };
      setMeetings(prevMeetings => [...prevMeetings, meeting]);
    }
  };

  return {
    meetings,
    updateMeeting,
    addMeeting,
  };
}